<template>
  <div>
    <v-row
      v-if="groupsEnabled"
      :class="[$vuetify.breakpoint.xsOnly ? 'flex-column-reverse' : 'flex-nowrap']"
    >
      <v-col :cols="12" :sm="9" :md="7" :lg="6">
        <v-select
          v-model="groupId"
          :items="questionnaireGroups"
          :disabled="disabled"
          label="Question Group"
        >
          <template v-slot:item="{ item, parent }">
            <v-list-item :disabled="item.disabled" class="pointer-events--auto" @click="item.disabled ? undefined : parent.$emit('select', item.value)">
              <v-row v-tooltip:right="{ content: item.disabledError, nudgeLeft: 300 }">
                <v-col v-tooltip:top="{ content: $t(`form.questionnaire.questionnaireGroups.blankQuestionTooltipMessage.${item.value}`), nudgeLeft: 300 }">
                  {{ item.text }}
                </v-col>
              </v-row>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
    </v-row>
    <component
      v-if="blockTypeComponent"
      v-model="blockData"
      :is="blockTypeComponent"
      :type="type"
      :allowed="allowed"
      :disabled="disabled"
      @valid="(val) => $emit('valid', val)"
    >
      <template v-slot:actions>
        <v-row class="flex-column pl-6 pr-1">
          <v-col shrink>
            <variant-picker
              v-model="activeQuestionnaireVariant"
              entity-type="block"
              :selected-questionnaire-variant="selectedQuestionnaireVariant"
              :variants="variants"
            />
          </v-col>
          <v-col shrink>
            <slot name="actions" />
          </v-col>
        </v-row>
      </template>
    </component>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import VFormBase from 'vuetify-form-base'
import { QuestionBlockData } from '@/types/Question'
import { PlatformBlock, PlatformQuestionnaireVariant, QuestionnaireVariantCode } from '@/types/Platform'
import { BlockTypes, BlockTypeItems, EditableBlockVariantDataFields } from '@/helpers/questionnaire/blocks'
import QuestionBlockForm from '@/components/forms/blocks/QuestionBlockForm.vue'
import VariantPicker from '@/components/shared/VariantPicker.vue'
import { QuestionnaireGroup } from '@betterboards/shared/types/Questionnaire'
import { PlatformQuestionnaire } from '@betterboards/shared/types/Platform'

export default Vue.extend({
  name: 'BlockForm',
  props: {
    type: { type: String, required: true },
    blockVariants: { type: Object as PropType<{ [variantCode: string]: PlatformBlock }>, required: true }, // Array of variants of this block
    allowed: { type: Array, required: false },
    selectedQuestionnaireVariant: { type: String as PropType<QuestionnaireVariantCode | undefined>, required: false },
    sectionId: { type: String, required: true },
    variants: { type: Array as PropType<PlatformQuestionnaireVariant[]>, required: true },
    groupsEnabled: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  components: {
    VariantPicker,
    VFormBase
  },
  data: () => ({
    activeQuestionnaireVariant: <QuestionnaireVariantCode | undefined>undefined,
    BlockTypes,
    BlockTypeItems
  }),
  mounted (): void {
    this.activeQuestionnaireVariant = this.selectedQuestionnaireVariant
  },
  computed: {
    selected (): PlatformQuestionnaire {
      return this.$store.state.Questionnaire.selected
    },
    block: {
      get (): PlatformBlock | undefined {
        if (!this.activeQuestionnaireVariant) {
          return
        }
        const defaultVariant = this.variants[0]?.variantCode
        const block = this.blockVariants[this.activeQuestionnaireVariant]
        if (defaultVariant === this.activeQuestionnaireVariant) {
          return block
        }
        const defaultBlock = this.blockVariants[defaultVariant]
        if (!defaultBlock || !block) {
          return
        }
        const data = {
          ...defaultBlock.data
        }
        EditableBlockVariantDataFields.forEach((field) => {
          data[field] = block.data?.[field] ?? defaultBlock.data?.[field]
        })
        return {
          ...block,
          data
        }
      },
      set (block: PlatformBlock): void {
        this.syncBlockVariantChanges(block)
        this.$emit('input', {
          ...block,
          companyId: this.$store.state.Company.selectedCompany.id,
          variantCode: this.activeQuestionnaireVariant ?? block.variantCode
        })
      }
    },
    blockData: {
      get (): QuestionBlockData | undefined {
        return this.block?.data
      },
      set (data: QuestionBlockData): void {
        this.block = {
          ...this.block,
          data
        } as any
      }
    },
    groupId: {
      get (): string | null | undefined {
        return this.block?.groupId
      },
      set (groupId: string | undefined): void {
        console.log('Setting groupId:', groupId)
        this.block = {
          ...this.block,
          groupId
        } as any
      }
    },
    blockTypeComponent (): Vue.Component | null {
      if (!this.block) {
        return null
      }
      switch (this.block.variant) {
        case BlockTypes.Question:
          return QuestionBlockForm
        default:
          return null
      }
    },
    questionnaireGroups (): Array<{value: string | null, text: string}> {
      if (!this.selected) {
        return []
      }
      const groups: QuestionnaireGroup[] = typeof this.selected.groups === 'string'
        ? JSON.parse(this.selected.groups)
        : this.selected.groups

      return [
        ...groups
          .filter((g) => g.variantCode === this.activeQuestionnaireVariant)
          .map((group) => ({
            value: group.id,
            text: group.name
          })),
        {
          value: null,
          text: this.$t('shared.blocks.repeatFor.options.None') as string
        }
      ]
    }
  },
  methods: {
    /**
     * Synchronizes any changes made to a block variant to the default variant of that block, as the `block` getter in this component
     *  uses the default block's data as the current state of all variants of that block. These changes are synced across all blocks
     *  in QuestionnaireBuilder:syncChangedBlockVariants() upon submitting the form.
     */
    syncBlockVariantChanges (block: PlatformBlock): void {
      const defaultVariant = this.variants[0]?.variantCode
      if (!block?.data || defaultVariant === this.activeQuestionnaireVariant) {
        return
      }
      const defaultBlock = this.blockVariants[defaultVariant]
      if (!defaultBlock) {
        return
      }
      const data = {
        ...defaultBlock.data
      }
      let changed = false
      Object.keys(block.data).forEach((field) => {
        if (EditableBlockVariantDataFields.includes(field)) {
          return
        }
        if (data[field] !== block.data[field]) {
          changed = true
        }
        data[field] = block.data[field]
      })
      if (changed) {
        this.$emit('input', {
          ...defaultBlock,
          data
        })
      }
    }
  }
})
</script>
